import React, { useRef, useCallback, useEffect, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMyDialog } from "@/context/MyDialogProvider";
import { Reader } from "./Reader";

type NewsItem = {
  id: number;
  title: string;
  link: string;
  description: string;
  source: string;
  published: string;
  image: string;
};

export default function MvNews() {
    const { open } = useMyDialog();
  const loaderRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const [autoLoad, setAutoLoad] = useState(true);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["mv-news", search],

    queryFn: async ({ pageParam = 1 }) => {
  const url = new URL(
    "https://script.google.com/macros/s/AKfycbyebMxl7upRCByWwZw9-XQGC4eYTtpzL0q1Pcf9AqV1YQo0_zptxA2BgdivSz3JkXzPcg/exec"
  );

  url.searchParams.set("page", String(pageParam));
  url.searchParams.set("limit", "20");

  if (search) {
    url.searchParams.set("q", search);
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  if (!res.ok) {
    throw new Error("Failed to load MV news");
  }

  const json = await res.json();

  return {
    success: json.success,
    data: json.data ?? [],
    page: json.page,
    limit: json.limit,
    hasMore: json.hasMore,
    nextCursor: json.nextCursor,
  };
},

    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextCursor : undefined;
    },

    initialPageParam: 1,
  });

  const items = data?.pages.flatMap((p) => p.data) || [];

  const isLoadingMore =
    isFetchingNextPage || (isFetching && !isLoading);

  // reset when search changes
  useEffect(() => {
    refetch();
  }, [search]);

  // infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];

      if (
        target.isIntersecting &&
        hasNextPage &&
        !isLoadingMore &&
        autoLoad
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isLoadingMore, fetchNextPage, autoLoad]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "100px",
      threshold: 0,
    });

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [handleObserver]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

 const viewDetails = (url: string) => {
  open({
    content: <Reader url={url} />,
  });
};

  return (
    <div dir="rtl" className="container mx-auto px-4 py-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-3 justify-between items-center mb-6">

        <div>
          <h1 className="text-3xl font-bold dark:text-white">
            Mv News
          </h1>
        </div>

   
      </div>

      {/* List */}
<div className="flex flex-col gap-3">

  {items.map((item) => (
    <div
      key={item.id}
      onClick={() => viewDetails(item.link)}
      className="flex w-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow hover:shadow-md transition cursor-pointer"
    >

      {/* LEFT SIDE (4/5) */}
      <div className="w-4/5 p-4 flex flex-col justify-between">

        {/* meta */}
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span>{item.source}</span>

          <span>
            {item.published?.slice(0, 10)}
            {item.published?.slice(0, 10) !==
              item.published?.slice(11, 16) &&
              ` | ${item.published?.slice(11, 16)}`}
          </span>
        </div>

        {/* title */}
        <h2 className="font-bold text-base md:text-lg text-gray-900 dark:text-white mt-2 line-clamp-2">
          {item.title}
        </h2>

        {/* description */}
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-2">
          {item.description}
        </p>

      </div>

      {/* RIGHT SIDE IMAGE (1/5) */}
      <div className="w-1/5 min-w-[90px] max-w-[140px]">
        {item.image ? (
          <img
            src={item.image}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 dark:bg-gray-800" />
        )}
      </div>

    </div>
  ))}

</div>

      {/* Loader */}
      <div ref={loaderRef} className="py-10 text-center">
        {hasNextPage ? (
          isLoadingMore ? (
            <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          ) : (
            <button
              onClick={() => fetchNextPage()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg"
            >
              Load More
            </button>
          )
        ) : (
          <p className="text-gray-500">End of news</p>
        )}
      </div>

    </div>
  );
}